import {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  type RefObject
} from 'react';

type UseTruncatedTooltipResult = {
  ref: RefObject<HTMLElement | null>;
  isTruncated: boolean;
  tooltipWidth: number | undefined;
};

export function useTruncatedTooltip(text: string): UseTruncatedTooltipResult {
  const ref = useRef<HTMLElement | null>(null);
  const [tooltipWidth, setTooltipWidth] = useState<number | undefined>(
    undefined
  );
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    setTooltipWidth(el.offsetWidth);
    setIsTruncated(
      el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight
    );
  }, []);

  useLayoutEffect(() => {
    checkTruncation();
    const innerRafId = { current: null as number | null };
    const raf1 = requestAnimationFrame(() => {
      innerRafId.current = requestAnimationFrame(checkTruncation);
    });
    const el = ref.current;
    if (!el) {
      return () => {
        cancelAnimationFrame(raf1);
        if (innerRafId.current != null) {
          cancelAnimationFrame(innerRafId.current);
        }
      };
    }
    const ro = new ResizeObserver(checkTruncation);
    ro.observe(el);
    const parent = el.parentElement;
    if (parent) {
      ro.observe(parent);
    }
    return () => {
      cancelAnimationFrame(raf1);
      if (innerRafId.current != null) {
        cancelAnimationFrame(innerRafId.current);
      }
      ro.disconnect();
    };
  }, [text, checkTruncation]);

  useEffect(() => {
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [checkTruncation]);

  return { ref, isTruncated, tooltipWidth };
}
